import React from 'react'
import {
  ComponentConfig,
  ComponentData,
  Slot,
  SlotComponent,
  usePuck,
} from '@puckeditor/core'
import { UploadedFile } from 'breaktime-theme/src/puck/fields/FileUploadField'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import {
  BorderOverlayProps,
  BorderStyleProps,
  ShadowProps,
  TypographyProps,
  borderOverlayField,
  borderOverlayStyle,
  borderStyleField,
  shadowStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import { cleanRichtext, richtextField } from '../ReferralCard/ReferralCard'
import { PQ_DISALLOW, SlotHint, pqCardStyle } from '../PQResult/PQResult'
import {
  PQSkipLogicRow,
  isDecisionTree,
  questionSkipLogicField,
} from '../PQAnswer/PQAnswer'

export type PQQuestionProps = {
  id?: string
  text?: string
  /** Per-question step background (legacy question-background-utils). */
  backgroundColor?: string
  backgroundImage?: UploadedFile | string
  mobileBackgroundImage?: UploadedFile | string
  /** The question card itself (the "white card") — content styles it via
   * customCss today. */
  cardBackground?: string
  cardTextColor?: string
  cardBorder?: BorderStyleProps
  cardShadow?: ShadowProps
  /** Card-wide font (question headline + answers inherit). */
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  /** Headline-only font (does not touch answers). */
  headlineFontFamily?: TypographyProps['fontFamily']
  headlineFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  /** Fixed height for the header area (image crops to it), e.g. 270px. */
  headerHeight?: string
  /** Width applied to every answer (e.g. 32% for 3-up image choices). */
  answerWidth?: string
  /** Flex layout of the answers list. */
  answersJustify?: string
  answersAlign?: string
  answersGap?: string
  /** Fill color of the selected/checked answer radio (legacy purple default). */
  selectedAnswerColor?: string
  borderOverlay?: BorderOverlayProps
  /** Decision Tree mode: attribute gates — the question is hidden once the
   * user's accumulated score exceeds a gate value (legacy keyValuePairs). */
  skipLogic?: PQSkipLogicRow[]
  /** Header media above the question (legacy `question.image`). */
  header: Slot
  answers: Slot
}

export const PQ_QUESTION_DEFAULT_PROPS: PQQuestionProps = {
  text: 'New question?',
  header: [],
  answers: [],
}

/**
 * One quiz question: header media, question text, answers list, optional
 * per-question step background. Editor shows the legacy slide markup with a
 * progress preview; production runs one slide at a time via the extended
 * legacy PersonalityQuizStep3.
 */
export const PuckPQQuestion: ComponentConfig<{
  props: PQQuestionProps
  fields: ThemeCustomFields
}> = {
  label: 'Personality Quiz Question',
  defaultProps: PQ_QUESTION_DEFAULT_PROPS,
  fields: {
    text: richtextField('Question Text'),
    // "slide" = the full-step backdrop BEHIND the white question card
    // (legacy semantics — not the card's own background)
    backgroundColor: {
      type: 'colorPicker',
      label: 'Slide Background Color (behind the card)',
      clearable: true,
    },
    backgroundImage: { type: 'fileUpload', label: 'Slide Background Image' },
    mobileBackgroundImage: {
      type: 'fileUpload',
      label: 'Slide Background Image (mobile)',
    },
    cardBackground: {
      type: 'colorPicker',
      label: 'Card Background',
      clearable: true,
      alpha: true,
    },
    cardTextColor: {
      type: 'colorPicker',
      label: 'Card Text Color',
      clearable: true,
    },
    cardBorder: { ...borderStyleField, label: 'Card Border' },
    cardShadow: { ...shadowStyleField, label: 'Card Shadow' },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    headlineFontFamily: { ...fontFamilyField, label: 'Headline Font Family' },
    headlineFontFamilyFallback: {
      ...fontFamilyFallbackField,
      label: 'Headline Font Fallback',
    },
    headerHeight: {
      type: 'text',
      label: 'Header Height (empty = auto)',
      placeholder: '270px',
    },
    answerWidth: {
      type: 'text',
      label: 'Answer Width (empty = theme default)',
      placeholder: '32%',
    },
    answersJustify: {
      type: 'select',
      label: 'Answers Justify',
      options: [
        { label: 'Default', value: '' },
        { label: 'Start', value: 'flex-start' },
        { label: 'Center', value: 'center' },
        { label: 'End', value: 'flex-end' },
        { label: 'Space Between', value: 'space-between' },
        { label: 'Space Around', value: 'space-around' },
        { label: 'Space Evenly', value: 'space-evenly' },
      ],
    },
    answersAlign: {
      type: 'select',
      label: 'Answers Align',
      options: [
        { label: 'Default', value: '' },
        { label: 'Start', value: 'flex-start' },
        { label: 'Center', value: 'center' },
        { label: 'End', value: 'flex-end' },
        { label: 'Space Between', value: 'space-between' },
        { label: 'Space Around', value: 'space-around' },
        { label: 'Space Evenly', value: 'space-evenly' },
      ],
    },
    answersGap: { type: 'text', label: 'Answers Gap', placeholder: '12px' },
    selectedAnswerColor: {
      type: 'colorPicker',
      label: 'Selected Answer Color',
      clearable: true,
    },
    borderOverlay: borderOverlayField,
    header: { type: 'slot', label: 'Header Media', disallow: PQ_DISALLOW },
    answers: { type: 'slot', allow: ['PQAnswer'] },
  },
  // Skip-logic gates only exist in Decision Tree mode.
  resolveFields: (_data, { appState, fields }) => {
    const data =
      (appState as { data?: { content?: ComponentData[] } }).data ?? {}
    const { skipLogic: _sl, ...rest } = fields
    return (
      isDecisionTree(data)
        ? { ...rest, skipLogic: questionSkipLogicField }
        : rest
    ) as typeof fields
  },
  render: props => <PQQuestionRender {...props} />,
}

type PQQuestionRenderProps = Omit<PQQuestionProps, 'header' | 'answers'> & {
  header: SlotComponent
  answers: SlotComponent
}


/** Editor-only render — legacy question-slide markup, stacked. */
function PQQuestionRender(props: PQQuestionRenderProps) {
  const {
    id,
    text,
    backgroundColor,
    header: Header,
    answers: Answers,
  } = props
  // Mirror production progress numbering from the stored questions list.
  const puck = usePuck()
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'PQContainer')
  const containerOptions = container?.props as
    | { hideProgressBar?: boolean; answerHoverColor?: string }
    | undefined
  const questions = ((container?.props as { questions?: ComponentData[] })
    ?.questions ?? []) as ComponentData[]
  const index = questions.findIndex(q => q.props?.id === id)
  const position = index >= 0 ? index + 1 : 1
  const total = questions.length || 1
  const stored = index >= 0 ? questions[index] : undefined
  const isSlotEmpty = (name: 'header' | 'answers') =>
    !((stored?.props as Record<string, ComponentData[] | undefined>)?.[name]
      ?.length)

  const textHtml = cleanRichtext(text)

  return (
    <div
      className={`step-3-quiz-item quiz__item bg-img with-shadow`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="quiz__question no-question-img" style={pqCardStyle(props)}>
        {borderOverlayStyle(props.borderOverlay) && (
          <div aria-hidden style={borderOverlayStyle(props.borderOverlay)} />
        )}
        <div
          className="quiz__question-header"
          style={
            props.headerHeight
              ? { height: props.headerHeight, overflow: 'hidden' }
              : undefined
          }
        >
          {/* static progress preview — production animates through slides */}
          {!containerOptions?.hideProgressBar && (
            <div className="progress-bar" aria-hidden>
              <span className="progress-label">Quiz</span>
              <span className="quiz__progress-number">
                {position} of {total}
              </span>
            </div>
          )}
          <div className="quiz__question-header-image">
            <SlotHint
              hint="Header media (optional) — drop an Image / Video here"
              isEmpty={isSlotEmpty('header')}
            >
              <Header disallow={PQ_DISALLOW} minEmptyHeight={32} />
            </SlotHint>
          </div>
        </div>
        <div className="quiz__question-content">
          {textHtml && (
            <h2
              className="quiz-question-headline"
              style={{
                ...applyTypography({
                  fontFamily: props.headlineFontFamily,
                  fontFamilyFallback: props.headlineFontFamilyFallback,
                }),
                ...(props.cardTextColor ? { color: props.cardTextColor } : {}),
              }}
              dangerouslySetInnerHTML={{ __html: textHtml }}
            />
          )}
          <ul
            className={`quiz__question-list${
              containerOptions?.answerHoverColor || props.selectedAnswerColor
                ? ' puck-pq-answers'
                : ''
            }`}
            style={{
              ...(containerOptions?.answerHoverColor
                ? ({
                    '--pq-answer-hover-bg': containerOptions.answerHoverColor,
                  } as React.CSSProperties)
                : {}),
              ...(props.selectedAnswerColor
                ? ({
                    '--pq-answer-selected': props.selectedAnswerColor,
                  } as React.CSSProperties)
                : {}),
              ...(props.answersJustify
                ? { justifyContent: props.answersJustify }
                : {}),
              ...(props.answersAlign ? { alignItems: props.answersAlign } : {}),
              ...(props.answersGap ? { gap: props.answersGap } : {}),
            }}
          >
            <SlotHint
              hint="Drop Quiz Answers here"
              isEmpty={isSlotEmpty('answers')}
            >
              <Answers allow={['PQAnswer']} minEmptyHeight={48} />
            </SlotHint>
          </ul>
        </div>
      </div>
    </div>
  )
}
